/**
 * featureGate.ts — Kombi-Gate aus DB-Flag + Mistral-Konfiguration.
 *
 * Diese Datei ist die einzige Stelle, an der das UI prüft, ob LLM-Vorschläge
 * verfügbar sind. So bleibt der Aufrufer einfach:
 *
 *   const { available, disabledReason } = useLlmFeatureState();
 *   if (!available) return <DisabledTooltip text={disabledReason} />;
 *
 * Im Backend selbst (mistralClient) ist das Gate redundant nochmal eingebaut
 * (defense-in-depth) — wenn das UI versehentlich aufruft trotz disabled,
 * blockt der Client.
 */

import { useEffect, useState } from 'react';

import { getFeatureFlag } from '../supabaseFeatureFlags';
import { getActiveProvider, getActiveProviderLabel, isLlmConfigured } from './llmClient';
import type { LlmProviderName } from './llmClient';

const FLAG_KEY = 'llm_suggestions';
const POLL_INTERVAL_MS = 30_000;

export interface LlmFeatureState {
  /** Flag-Wert in der DB. true wenn aktiviert. */
  readonly enabled: boolean;
  /** Mindestens ein LLM-Provider-API-Key gesetzt? */
  readonly llmConfigured: boolean;
  /** Welcher Provider ist aktiv (anthropic/mistral)? null wenn keiner. */
  readonly activeProvider: LlmProviderName | null;
  /** Anzeige-Label für UI ("Claude Sonnet (Anthropic)" etc.) */
  readonly activeProviderLabel: string;
  /** Backward-compat: true wenn Mistral-Provider aktiv ODER Anthropic-Key gesetzt. */
  readonly mistralConfigured: boolean;
  /** true wenn beide Gates offen sind (UI darf den Vorschlags-Button anzeigen). */
  readonly available: boolean;
  /** Erklärung warum nicht verfügbar — für UI-Tooltip; null wenn available. */
  readonly disabledReason: string | null;
  /** true während des ersten async Loads — UI zeigt Skeleton. */
  readonly isLoading: boolean;
}

const LOADING_STATE: LlmFeatureState = {
  enabled: false,
  llmConfigured: false,
  activeProvider: null,
  activeProviderLabel: 'wird geladen',
  mistralConfigured: false,
  available: false,
  disabledReason: null,
  isLoading: true,
};

function computeReason(enabled: boolean, configured: boolean): string | null {
  if (!enabled && !configured) {
    return 'LLM-Vorschläge sind im System deaktiviert (Admin-Flag) UND kein LLM-Provider-API-Key ist im Deployment gesetzt.';
  }
  if (!enabled) {
    return 'LLM-Vorschläge sind aktuell deaktiviert. Admin kann das Feature in den System-Settings einschalten.';
  }
  if (!configured) {
    // WICHTIG: VITE_-Variablen werden zur Build-Zeit ins öffentliche Bundle
    // inlined — ein hier hinterlegter Key ist für jeden Besucher lesbar. Der
    // client-seitige Pfad ist nur für den EU-Provider (Mistral) im Pilotbetrieb
    // gedacht; für Echtdaten gehört der Key hinter einen EU-Server-Proxy.
    return 'Kein zulässiger LLM-Provider konfiguriert (client-seitig nur EU-Provider Mistral). Für Echtdaten Key serverseitig hinter EU-Proxy legen, nicht als VITE_-Variable.';
  }
  return null;
}

/**
 * Async Snapshot — für Code außerhalb von React (Tests, async event handlers).
 */
export async function getLlmFeatureState(): Promise<LlmFeatureState> {
  const configured = isLlmConfigured();
  const provider = getActiveProvider();
  const label = getActiveProviderLabel();
  let enabled = false;
  try {
    enabled = await getFeatureFlag(FLAG_KEY);
  } catch (e) {
    // Fail-closed: bei DB-Fehler nicht verfügbar.
    console.error('getLlmFeatureState: getFeatureFlag warf', e);
    enabled = false;
  }
  const available = enabled && configured;
  return {
    enabled,
    llmConfigured: configured,
    activeProvider: provider,
    activeProviderLabel: label,
    mistralConfigured: configured, // Backward-compat
    available,
    disabledReason: computeReason(enabled, configured),
    isLoading: false,
  };
}

/**
 * React-Hook: pollt alle 30 s den DB-Flag. ENV-Var ändert sich nicht zur
 * Laufzeit (Build-Time-Injection), daher reicht einmal lesen am Mount.
 *
 * Während des ersten Loads ist `isLoading=true`, `available=false` — UI
 * kann Skeleton oder neutrales State zeigen.
 */
export function useLlmFeatureState(): LlmFeatureState {
  const [state, setState] = useState<LlmFeatureState>(LOADING_STATE);

  useEffect(() => {
    let cancelled = false;

    const tick = async (): Promise<void> => {
      const next = await getLlmFeatureState();
      if (!cancelled) setState(next);
    };

    void tick();
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, []);

  return state;
}
